import { Box, BoxProps, Button, Flex, Spacer, Text, useToast } from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";

import { TableAction } from "./table";

interface SelectTableActionProps<TableRowType> extends BoxProps {
  tableActions: TableAction<TableRowType>[];
  rows: TableRowType[];
  deselectAll?: () => void;
  icon?: React.ReactNode;
}

export function SelectTableAction<TableRowType>({
  tableActions,
  rows,
  width,
  deselectAll,
  icon,
  ...rest
}: SelectTableActionProps<TableRowType>) {
  const toast = useToast();
  const [fixedStyling, setFixedStyling] = useState<boolean>();
  const [deleteSelectedDivWidth, setDeleteSelectedDivWidth] = useState<number>(0);
  const deleteSelectedHeightDiv = useRef<HTMLDivElement>(null);
  const deleteSelectedOverflowDiv = useRef<HTMLDivElement>(null);

  const scrollEventListener = () => {
    if (!deleteSelectedHeightDiv.current || !deleteSelectedOverflowDiv.current) return;
    setFixedStyling(deleteSelectedHeightDiv.current.getBoundingClientRect().top < 0);
  };

  const resizeEventListener = () =>
    setDeleteSelectedDivWidth(deleteSelectedHeightDiv.current?.clientWidth || 0);

  useEffect(() => {
    scrollEventListener();
    resizeEventListener();
    document.addEventListener("scroll", scrollEventListener);
    window.addEventListener("resize", resizeEventListener);
    return () => {
      document.removeEventListener("scroll", scrollEventListener);
      window.removeEventListener("resize", resizeEventListener);
    };
  }, []);

  const defaultHeight = "60px";
  return (
    <>
      <Flex
        ref={deleteSelectedHeightDiv}
        height={fixedStyling ? defaultHeight : 0}
        width="100%"
      ></Flex>
      <Flex
        width={deleteSelectedDivWidth}
        height={defaultHeight}
        visibility={rows.length > 0 ? "visible" : "hidden"}
        fontWeight={600}
        background="#F5B2B2"
        position={fixedStyling ? "fixed" : "relative"}
        top="0"
        left={fixedStyling ? deleteSelectedHeightDiv.current?.getBoundingClientRect().left : "0"}
        zIndex={50}
        {...rest}
      >
        <Box padding={18} whiteSpace="nowrap">
          {icon ? <span style={{ paddingRight: "5px" }}>{icon}</span> : null} {rows.length}{" "}
          selected.
        </Box>
        <Spacer />
        <Flex overflowX="auto" overflowY="hidden" ref={deleteSelectedOverflowDiv}>
          {tableActions.map((tableAction, idx) => (
            <Button
              variant="ghost"
              key={`tableAction-${idx}`}
              isDisabled={tableAction.isDisabledTitle !== undefined}
              title={tableAction.isDisabledTitle}
              onClick={() => {
                try {
                  tableAction.action(rows);
                } catch (e) {
                  toast({
                    title: "Something went wrong while deleting.",
                    status: "error",
                    duration: 9000,
                    isClosable: true,
                  });
                  console.error(e);
                }
              }}
              padding={0}
              marginTop="8px"
              paddingLeft="8px"
              paddingRight="8px"
              w={tableAction.label.length > 15 ? "170px" : "auto"}
            >
              {tableAction.icon}{" "}
              <Text
                noOfLines={tableAction.label.length > 15 ? 2 : 1}
                width="max-content"
                whiteSpace="pre-wrap"
                textAlign="left"
                m={0}
              >
                {tableAction.label} ({rows.length})
              </Text>
            </Button>
          ))}
          {deselectAll && (
            <Button
              variant="ghost"
              onClick={() => deselectAll()}
              padding={0}
              marginTop="8px"
              paddingLeft="8px"
              paddingRight="8px"
              marginRight="8px"
              width="min-content"
            >
              Deselect all
            </Button>
          )}
        </Flex>
      </Flex>
    </>
  );
}
